import type { TaskRow } from "@shared/types";
import { parseDateLocal, startOfDay, addDays } from "./format";

/**
 * Buckets tasks by due-date urgency for the My Tasks view. Date-only comparison
 * goes through parseDateLocal + startOfDay so a "2026-06-13" due date is judged
 * in the viewer's local day, not UTC (avoids the off-by-one west of UTC).
 */
export type DueGroupKey = "overdue" | "today" | "week" | "later" | "nodate";

export interface DueGroup {
  key: DueGroupKey;
  label: string;
  tasks: TaskRow[];
}

const LABELS: Record<DueGroupKey, string> = {
  overdue: "Overdue",
  today: "Today",
  week: "This week",
  later: "Later",
  nodate: "No due date",
};

// Render order. "This week" means the next 7 days after today (today itself is
// its own bucket).
export const DUE_GROUP_ORDER: DueGroupKey[] = [
  "overdue",
  "today",
  "week",
  "later",
  "nodate",
];

export function bucketForDue(
  due: string | null | undefined,
  now: Date,
): DueGroupKey {
  if (!due) return "nodate";
  const parsed = parseDateLocal(due);
  if (!parsed) return "nodate";
  const day = startOfDay(parsed).getTime();
  const today = startOfDay(now).getTime();
  if (day < today) return "overdue";
  if (day === today) return "today";
  const weekEnd = startOfDay(addDays(startOfDay(now), 7)).getTime();
  if (day <= weekEnd) return "week";
  return "later";
}

/**
 * Groups tasks into the five due buckets in render order. Empty buckets are
 * included so callers can decide whether to show a zero count or skip them.
 */
export function groupByDue(tasks: TaskRow[], now: Date): DueGroup[] {
  const buckets: Record<DueGroupKey, TaskRow[]> = {
    overdue: [],
    today: [],
    week: [],
    later: [],
    nodate: [],
  };
  for (const t of tasks) {
    buckets[bucketForDue(t.dueDate, now)].push(t);
  }
  return DUE_GROUP_ORDER.map((key) => ({
    key,
    label: LABELS[key],
    tasks: buckets[key],
  }));
}
